import { buildImpact } from "@/components/FormEngine/helpers/buildImpacts";
import { FieldImpact, FormOption } from "@/components/FormEngine/types";
import {
  isNonEmptyString,
  isObject,
  sanitizeIdPart,
} from "@/components/FormEngine/utils";

// ========================================
// TYPES ET INTERFACES
// ========================================

/**
 * Structure d'une option brute provenant d'une source externe
 * Les propriétés peuvent avoir différents noms selon la source
 */
interface RawOptionData {
  /** Identifiant possible de l'option */
  id?: unknown;
  /** Valeur possible de l'option */
  value?: unknown;
  /** Texte affiché possible de l'option */
  text?: unknown;
  /** Champs impactés par cette option */
  impactedFields?: unknown;
  /** Autres propriétés possibles */
  [key: string]: unknown;
}

/**
 * Option par défaut retournée en cas d'erreur
 */
const DEFAULT_EMPTY_OPTION: FormOption = {
  id: "",
  value: "",
};

// ========================================
// FONCTION PRINCIPALE
// ========================================

/**
 * Construit une option de formulaire normalisée à partir de données brutes
 *
 * Cette fonction gère différents formats d'entrée et normalise les données
 * pour créer une structure FormOption cohérente.
 *
 * Ordre de priorité pour l'ID : id > value > text
 * Ordre de priorité pour la valeur affichée : text > value > id
 *
 * @param rawOption - Les données brutes de l'option (format inconnu)
 * @returns Une option de formulaire normalisée
 *
 * @example
 * // Entrée simple
 * buildOption({ id: "opt1", text: "Option 1" })
 * // Retourne: { id: "opt1", value: "Option 1" }
 *
 * @example
 * // Avec impacts
 * buildOption({
 *   id: "yes",
 *   text: "Oui",
 *   impactedFields: [{ name: "field1", properties: { hidden: true } }]
 * })
 * // Retourne: {
 * //   id: "yes",
 * //   value: "Oui",
 * //   impacts: [{ name: "field1", properties: { hidden: true } }]
 * // }
 */
export const buildOption = (rawOption: unknown): FormOption => {
  // Validation : vérifier que l'entrée est un objet
  if (!isValidOptionInput(rawOption)) {
    return DEFAULT_EMPTY_OPTION;
  }

  // Typer l'option brute pour un accès plus facile aux propriétés
  const rawOptionData = rawOption as RawOptionData;

  // Étape 1 : Extraire et normaliser l'identifiant
  const optionId = extractAndSanitizeOptionId(rawOptionData);

  // Étape 2 : Extraire la valeur affichée
  const displayValue = extractDisplayValue(rawOptionData);

  // Étape 3 : Créer l'option de base
  const formOption: FormOption = createBaseOption(optionId, displayValue);

  // Étape 4 : Ajouter les impacts si présents
  const optionWithImpacts = addImpactsIfPresent(formOption, rawOptionData);

  return optionWithImpacts;
};

// ========================================
// FONCTIONS DE VALIDATION
// ========================================

/**
 * Vérifie si l'entrée est valide pour créer une option
 *
 * @param input - La valeur à vérifier
 * @returns true si l'entrée peut être utilisée pour créer une option
 */
function isValidOptionInput(input: unknown): boolean {
  return isObject(input);
}

// ========================================
// EXTRACTION DE L'IDENTIFIANT
// ========================================

/**
 * Extrait et nettoie l'identifiant de l'option
 *
 * Ordre de priorité : id > value > text
 * L'identifiant est ensuite nettoyé pour être utilisable dans le formulaire
 *
 * @param rawOptionData - Les données brutes de l'option
 * @returns L'identifiant nettoyé de l'option
 */
function extractAndSanitizeOptionId(rawOptionData: RawOptionData): string {
  // Extraire l'identifiant brut selon l'ordre de priorité
  const rawId = extractRawOptionId(rawOptionData);

  // Nettoyer l'identifiant pour qu'il soit valide
  const sanitizedId = sanitizeIdPart(rawId);

  return sanitizedId;
}

/**
 * Extrait l'identifiant brut de l'option selon l'ordre de priorité
 *
 * @param rawOptionData - Les données brutes de l'option
 * @returns L'identifiant brut (non nettoyé)
 */
function extractRawOptionId(rawOptionData: RawOptionData): string {
  // Priorité 1 : Utiliser la propriété 'id' si elle existe et est non vide
  if (hasValidStringProperty(rawOptionData, "id")) {
    return String(rawOptionData.id);
  }

  // Priorité 2 : Utiliser la propriété 'value' si elle existe et est non vide
  if (hasValidStringProperty(rawOptionData, "value")) {
    return String(rawOptionData.value);
  }

  // Priorité 3 : Utiliser la propriété 'text' si elle existe et est non vide
  if (hasValidStringProperty(rawOptionData, "text")) {
    return String(rawOptionData.text);
  }

  // Par défaut : chaîne vide
  return "";
}

/**
 * Vérifie si un objet a une propriété string non vide
 *
 * @param obj - L'objet à vérifier
 * @param propertyName - Le nom de la propriété
 * @returns true si la propriété existe et est une string non vide
 */
function hasValidStringProperty(
  obj: RawOptionData,
  propertyName: keyof RawOptionData,
): boolean {
  return isNonEmptyString(obj[propertyName]);
}

// ========================================
// EXTRACTION DE LA VALEUR AFFICHÉE
// ========================================

/**
 * Extrait la valeur qui sera affichée à l'utilisateur
 *
 * Ordre de priorité : text > value > id
 *
 * @param rawOptionData - Les données brutes de l'option
 * @returns La valeur à afficher
 */
function extractDisplayValue(rawOptionData: RawOptionData): string {
  // Priorité 1 : Utiliser 'text' si disponible
  if (rawOptionData.text !== undefined && rawOptionData.text !== null) {
    return String(rawOptionData.text);
  }

  // Priorité 2 : Utiliser 'value' si disponible
  if (rawOptionData.value !== undefined && rawOptionData.value !== null) {
    return String(rawOptionData.value);
  }

  // Priorité 3 : Utiliser 'id' si disponible
  if (rawOptionData.id !== undefined && rawOptionData.id !== null) {
    return String(rawOptionData.id);
  }

  // Par défaut : chaîne vide
  return "";
}

// ========================================
// CRÉATION DE L'OPTION
// ========================================

/**
 * Crée l'option de base avec l'ID et la valeur
 *
 * @param id - L'identifiant de l'option
 * @param value - La valeur affichée de l'option
 * @returns L'option de base
 */
function createBaseOption(id: string, value: string): FormOption {
  return {
    id: id,
    value: value,
  };
}

// ========================================
// GESTION DES IMPACTS
// ========================================

/**
 * Ajoute les impacts à l'option si des champs impactés sont définis
 *
 * @param baseOption - L'option de base
 * @param rawOptionData - Les données brutes contenant potentiellement des impacts
 * @returns L'option avec les impacts ajoutés (si présents)
 */
function addImpactsIfPresent(
  baseOption: FormOption,
  rawOptionData: RawOptionData,
): FormOption {
  // Extraire les champs impactés
  const impactedFields = extractImpactedFields(rawOptionData);

  // Si pas de champs impactés, retourner l'option de base
  if (!hasImpactedFields(impactedFields)) {
    return baseOption;
  }

  // Construire les impacts et les ajouter à l'option
  const impacts = buildImpactsFromFields(impactedFields);

  return {
    ...baseOption,
    impacts: impacts,
  };
}

/**
 * Extrait les champs impactés des données brutes
 *
 * @param rawOptionData - Les données brutes de l'option
 * @returns Les champs impactés ou undefined
 */
function extractImpactedFields(rawOptionData: RawOptionData): unknown {
  return rawOptionData.impactedFields;
}

/**
 * Vérifie si des champs impactés sont définis et valides
 *
 * @param impactedFields - Les champs impactés potentiels
 * @returns true si c'est un tableau valide de champs impactés
 */
function hasImpactedFields(
  impactedFields: unknown,
): impactedFields is unknown[] {
  return Array.isArray(impactedFields) && impactedFields.length > 0;
}

/**
 * Construit les objets d'impact à partir des champs impactés
 *
 * @param impactedFields - Le tableau des champs impactés
 * @returns Le tableau des impacts construits
 */
function buildImpactsFromFields(impactedFields: unknown[]): FieldImpact[] {
  return impactedFields.map((fieldData) => buildImpact(fieldData));
}
